import React, { useState } from "react";
import {
  Avatar,
  Button,
  Paper,
  Stack,
  SvgIcon,
  Typography,
} from "@mui/material";
import format from "date-fns/format";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import BoltIcon from "@mui/icons-material/Bolt";
import Superconnect from "../common/Superconnect";
import { useRouter } from "next/router";
import { resetScrollForRoute } from "../../utils/storage";
import EditDialog from "./EditDialog";
import ScListDialog from "../superconnections/ScListDialog";
import { pluralize } from "../../utils/text";
import VerifiedBadge from "../chfeed/VerifiedBadge";
import { trackEvent } from "../../utils/analytics";
import { analyticsConstants } from "../../constants";
import ChampionBadge from "../common/ChampionBadge";

type ProfileIntroCardPropsType = {
  isSameUser: boolean;
  image: string;
  name: string;
  headline: string;
  currentCompany: string;
  city: string;
  onboardingDate: Date;
  connected: boolean;
  memberId: string;
  connectionsCount: number;
  isVip: boolean;
  type?: string;
  champion: string;
};

function ProfileIntroCard({
  isSameUser,
  image,
  name,
  headline,
  currentCompany,
  city,
  onboardingDate,
  connected,
  memberId,
  connectionsCount = 0,
  isVip,
  type,
  champion,
}: ProfileIntroCardPropsType) {
  const router = useRouter();

  const [isScListDialogVisible, setIsScListDialogVisible] = useState(false);

  const onScCountClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setIsScListDialogVisible(true);
  };

  const onClose = () => {
    setIsScListDialogVisible(false);
  };

  return (
    <>
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: 2,
          position: "sticky",
          top: "72px",
        }}
      >
        <Stack alignItems="center">
          <Avatar
            src={image}
            alt="profile"
            sx={{
              width: { xs: 72, lg: 100 },
              height: { xs: 72, lg: 100 },
            }}
          />
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            my={1}
            flexWrap="wrap"
          >
            <Typography fontWeight="500" variant="h6">
              {name}
            </Typography>

            {isVip && <VerifiedBadge isVerified={isVip} />}
          </Stack>
          <Typography
            variant="caption"
            color="secondary"
            fontWeight="400"
            textAlign="center"
          >
            {headline}
          </Typography>
        </Stack>

        {isSameUser && (
          <EditDialog
            from="general"
            name={name}
            headline={headline}
            currentCompany={currentCompany}
            city={city}
            image={image}
          />
        )}
        <Stack direction="row" alignItems="center" mt={2}>
          <ChampionBadge
            isChampion={champion?.length > 0}
            champion={champion}
            fromProfile={true}
          />
        </Stack>
        <Stack direction="row" alignItems="center" mt={1}>
          <BusinessCenterOutlinedIcon fontSize="small" />
          <Typography
            variant="subtitle2"
            color="secondary"
            fontWeight="400"
            ml={0.8}
          >
            {currentCompany}
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" mt={1}>
          <LocationOnOutlinedIcon fontSize="small" />
          <Typography
            variant="subtitle2"
            color="secondary"
            fontWeight="400"
            ml={0.8}
          >
            {city}
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" mt={1}>
          <CalendarTodayOutlinedIcon fontSize="small" />
          <Typography
            variant="subtitle2"
            color="secondary"
            fontWeight="400"
            ml={0.8}
            textTransform="lowercase"
          >
            member since,{" "}
            {onboardingDate! && format(new Date(onboardingDate!), "LLL, yyyy")}
          </Typography>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          mt={1}
          sx={{ cursor: "pointer" }}
          onClick={onScCountClick}
        >
          <SvgIcon fontSize="medium">
            <path
              fill="currentColor"
              d="M21.1,12.5L22.5,13.91L15.97,20.5L12.5,17L13.9,15.59L15.97,17.67L21.1,12.5M11,4A4,4 0 0,1 15,8A4,4 0 0,1 11,12A4,4 0 0,1 7,8A4,4 0 0,1 11,4M11,6A2,2 0 0,0 9,8A2,2 0 0,0 11,10A2,2 0 0,0 13,8A2,2 0 0,0 11,6M11,13C11.68,13 12.5,13.09 13.41,13.26L11.74,14.93L11,14.9C8.03,14.9 4.9,16.36 4.9,17V18.1H11.1L13,20H3V17C3,14.34 8.33,13 11,13Z"
            />
          </SvgIcon>
          <Typography
            variant="subtitle2"
            color="secondary"
            fontWeight="500"
            ml={0.8}
            sx={{ textDecoration: "underline" }}
          >
            {connectionsCount}{" "}
            {pluralize(connectionsCount, "follower", "followers")}
          </Typography>
        </Stack>

        {!isSameUser && (
          <Stack mt={2} direction="row" flexWrap="wrap" spacing={1}>
            <Button
              variant="outlined"
              color="secondary"
              sx={{
                borderRadius: "24px",
                display: "flex",
                flex: 1,
                fontSize: 12,
              }}
              onClick={() => {
                //INFO remove any scroll stored position for other user activity
                trackEvent("view_activity_button_click", {
                  member_id: memberId,
                  member_type: type,
                  screen_name: analyticsConstants.SCREEN.PROFILE_PAGE,
                  tab_name: analyticsConstants.TAB.NETWORK,
                  element_name: "view_activity",
                  element_type: analyticsConstants.TYPE.BUTTON,
                  event_category: analyticsConstants.CATEGORY.CLICK,
                });
                resetScrollForRoute("/activity/", true);
                router.push(`/activity/${memberId}`);
              }}
            >
              view activity
            </Button>
            <Superconnect
              withTitle={true}
              isConnected={connected}
              otherUserId={memberId}
              otherUserName={name}
              from="profile"
              memberType={type}
            />
          </Stack>
        )}
      </Paper>
      {isScListDialogVisible && (
        <ScListDialog
          open={isScListDialogVisible}
          onClose={onClose}
          userId={memberId}
        />
      )}
    </>
  );
}

export default ProfileIntroCard;
